<template>
  <PanelRow class="sensorview">
    <div class="panel compact">
      <header class="center">
        <h4>{{ 'feelgrid:puredata.sensor_view' | trans }}</h4>
      </header>
      <ul class="sensors">
        <li v-for="type in available_types">
          <label class="radio">
            <Icon :name="icon(type)" />
            <span>{{ type.title | text }}</span>
            <input type="radio" :value="type.id" v-model="type_id" />
            <i></i>
          </label>
        </li>
      </ul>
      <template v-if="type">
        <header class="center">
          <h4>{{ type.title | text }}</h4>
        </header>
        <div class="controls">
          <DeviceSelector v-model="chosen_ids" :devices="available_devices" />
          <IntervalSelector
            v-model="time_interval"
            @reload="reload"
            @resolution="user_interval = $event"
            :resolution=user_interval
            :min_resolution=min_interval
          />
        </div>
      </template>
      <div class="data-table" v-if="type && chosen_ids.length">
        <p class="legend">
          <span>{{ 'feelgrid:puredata.legend.hover' | trans }}</span>
          <span class="faded">{{ 'feelgrid:puredata.legend.faded' | trans }}</span>
          <span>{{ 'feelgrid:puredata.legend.clear' | trans }}</span>
        </p>
        <div class="list-holder scrollable-table">
          <table v-if="list" class="itemstable">
            <thead><tr>
              <th class="time">{{ 'feelgrid:devices.data.fields.created_at' | trans }}</th>
              <th class="type" v-for="device in chosen_devices">{{ device.name | text }}</th>
              <th class="padder"></th>
            </tr></thead>
            <tbody>
              <tr v-for="point in list">
                <td class="time">{{ point.date }}</td>
                <td class="value type" v-for="device in chosen_devices" :title="point.values[device.id].time">
                  <span :class="point.values[device.id].class">
                    {{ point.values[device.id].value | text }}
                  </span>
                </td>
                <td class="padder"></td>
              </tr>
            </tbody>
          </table>
          <Loading v-else />
        </div>
        <Pages class="center" v-model="p" :pages=pages />
      </div>
    </div>
  </PanelRow>
</template>

<script>
  import DeviceSelector from '../../assets/DeviceSelector'
  import {sensor_value, sensor_icon} from '../../helpers'
  import {dateFromApi, date4api, dateformat} from '@/providers/fields'
  import {async} from '@/helpers/mixins/pagination'
  import base from './base'
  import _ from 'lodash'

  export default {
    name: 'SensorView',
    mixins: [base, async],
    components: {DeviceSelector},
    data () {return {
      type_id: false,
      chosen_ids: [],
      icon: sensor_icon,
      user_interval: false,
    }},
    computed: {
      available_types () {
        var t = {};
        for (let device of this.available_devices) {
          for (let type of _.get(device, 'item.hardware_types.data_types', [])) {
            t[type.id] = type;
          }
        }
        return t;
      },
      type () {
        if (!this.available_types || !this.type_id) {
          return;
        }
        return this.available_types[this.type_id];
      },
      baseurl () {
        if (this.type) {
          return 'devices_data' +
            '?include=' + _.snakeCase(this.type.name) +
            '&sort=id::desc' +
            '&range=all' +
            '&filter='
          ;
        }
      },
      page_range_filter () {
        var limit = 'created_at::mr::' + date4api(this.minmax_dates[0]);
        if (this.date_limits[1] !== this.now || this.p > 1) {
          limit += ',created_at::lseq::' + date4api(this.minmax_dates[1]);
        }
        return limit;
      },
      minmax_dates () {
        var removeSeconds = s => new Date(this.now.getTime() - s * 1000);
        //seconds on one page
        var page_interval = this.interval * this.perpage;
        //start and end of one page
        var start = removeSeconds(page_interval * this.p);
        start.setSeconds(0);
        var end = removeSeconds(page_interval * (this.p - 1));
        end.setSeconds(59);
        return [start, end];
      },
      min_date () {
        if (!this.min_dates) {
          return this.date_limits[0];
        }
        //minimum of minimum device dates, but more than user selected min date
        var min = Object.values(this.min_dates)
          .reduce(
            (acc, val) => val < acc ? val : acc,
            this.now
          )
        ;
        return min < this.date_limits[0] ? this.date_limits[0] : min;
      },
      chosen_devices () {
        return this.available_devices
          .filter(d => this.chosen_ids.includes(d.item.id))
        ;
      },
      interval () {
        if (this.user_interval) {
          return Math.max(this.user_interval, this.min_interval);
        }
        return this.min_interval;
      },
      min_interval () {
        //minumum interval in seconds
        var base_interval =
          this.chosen_devices
            .map(d => Number.parseInt(_.get(d, 'item.hardware_types.uplink_interval', null)))
            .reduce((acc, val) => val ? Math.min(acc, val) : acc, 3600)
        ;
        //minimum interval of chosen devices, but must be a multiple of 60 seconds (rounded down), but at least 60 seconds
        return Math.max(60, 60 * Math.floor(base_interval / 60));
      },
    },
    asyncComputed: {
      min_dates () {
        return Promise.all(this.available_devices.map(device =>
            //return a pair: [device_id, min_date]
            Promise.all([
              device.id,
              this.api.get(
                  'devices_data' +
                    '?filter=devices_id::eq::' + device.id +
                    '&sort=id::asc' +
                    '&range=0,1'
                )
                .then(r => dateFromApi(r.created_at))
            ])
          ))
          //transforms [[a, 1], [b, 2]] to {a:1, b:2}
          .then(_.fromPairs)
        ;
      },
      list: {
        get () {
          if (!this.baseurl || this.paginated === null) {
            return Promise.resolve(false);
          }
          this.loading = true;
          var i = this.p - 1;
          if (this.paginated !== null) {
            i = Math.min(i, Math.max(0, this.paginated.length));
            if (this.paginated[i] !== null) {
              this.loading = false;
              return new Promise(resolve => resolve(this.paginated[i]));
            }
          }
          var base = this.baseurl;
          //function that request URL for device ID
          var device_url = device_id =>
            this.baseurl + this.page_range_filter + ',devices_id::eq::' + device_id
          ;
          if (base) {
            this.list = null;
            if (base.substring(-1) !== '?') {
              base = base + (base.indexOf('?') > -1 ? '&': '?');
            }
            var p = Promise.all(this.chosen_ids.map(id =>
                this.api.get(device_url(id), this.cancel)
                  //pairs [[id, data], [id, data]]
                  .then(data => ([id, data]))
              ))
              .then(_.fromPairs)
              .then(data => {
                this.$set(this.paginated, i, this.arrange(data));
                this.loading = false;
                return this.paginated[i];
              })
              .catch(e => {
                this.$set(this.paginated, i, null);
                throw e;
              })
            ;
            this.$set(this.paginated, i, p);
            return p;
          }
        },
        watch () {
          this.reloadcount;
          this.baseurl;
          this.perpage;
          this.paginated;
        },
      }
    },
    methods: {
      get_formated (point) {
        var t = _.snakeCase(this.type.name);
        if (!point[t] || !point[t].length) {
          return null;
        }
        var reading = point[t].filtered('data_types_id', this.type.id);
        if (!reading.length) {
          return null;
        }
        return sensor_value(reading.value, this.type);
      },
      setall () {
        if (this.available_devices && this.available_devices.length && !this.chosen_ids.length) {
          this.chosen_ids = this.available_devices.map(d => d.id);
        }
        if (this.available_types && Object.keys(this.available_types).length && !this.type_id) {
          this.type_id = Object.keys(this.available_types)[0];
        }
      },
      // Transforms data from various devices in various times to a unified timescale
      arrange (data) {
        var dates = [];
        var dplus = (d, s) => new Date(d.getTime() + s*1000);
        var empty = {};
        //find the result between 2 times
        var find_result = (results, from, to) => {
          for (let r of results) {
            let d = dateFromApi(r.created_at);
            if (d <= to) {
              var value = this.get_formated(r);
              if (value !== null) {
                return {
                  value,
                  class: d > from ? '' : 'muted',
                  time: dateformat(d, 'DD.MM. HH:mm:ss'),
                };
              }
            }
          }
          return {
            class: 'muted',
            value: ''
          };
        };
        for (let id of this.chosen_ids) {
          empty[id] = '';
        }
        for (var d = this.minmax_dates[0]; d <= this.minmax_dates[1]; d = dplus(d, this.interval)) {
          let date = dateformat(d, 'DD.MM.YYYY HH:mm');
          let point = {
            date, values: Object.assign({}, empty)
          };
          for (let id of this.chosen_ids) {
            point.values[id] = find_result(data[id], d, dplus(d, this.interval));
          }
          dates.unshift(point);
        }
        return dates;
      },
      compute_pages (clear = false) {
        this.p = 1;
        if (clear && this.cancel.function) {
          this.cancel.function();
          this.cancel = {};
        }
        //difference in seconds between minumum and current time
        var diff = (this.now.getTime() - this.min_date.getTime()) / 1000 || 1;
        this.countAll = Math.ceil(diff / this.interval) || 0;
        var pages = Math.ceil(this.countAll / this.perpage) || 0;
        if (this.paginated === null || clear) {
          return this.paginated = new Array(pages).fill(null);
        }
        if (this.paginated.length > pages) {//remove some pages
          this.paginated.splice(pages);  //remove everything after last page
        } else if (this.paginated.length < pages) {//add some pages
          this.paginated = this.paginated.concat(
            //add missing pages as null
            new Array(pages - this.paginated.length).fill(null)
          );
        }
      },
    },
    watch: {
      min_date () {
        this.compute_pages(false);
      },
      interval () {
        this.compute_pages(true);
      },
      now () {
        this.compute_pages(true);
      },
      available_types: {
        deep: true,
        handler () {this.setall();},
      },
      'available_devices.length' () {
        this.setall();
      },
      paginated (val) {
        if (val === null) {
          this.compute_pages();
        }
      },
    },
    mounted () {
      this.setall();
    },
  }
</script>

<style lang="scss">
  @import '../../scss/variables';

  .feelgrid-puredata .sensorview {
    p.legend {
      @include font-size(16px);
      span {
        display: inline-block;
        &:not(:first-child) {
          border-left: solid $default-color 1px;
          padding-left: 6px;
          margin-left: 6px;
        }
      }
    }
    ul.sensors {
      text-align: left;
      display: block;
      display: grid;
      width: 100%;
      grid-template-columns: repeat(auto-fill, minmax(310px, 350px));
      grid-template-rows: auto;
      > li {
        grid-column: auto;
        display: inline-block;
        justify-self: center;
        width: 290px;
        padding: 0 30px;
        margin: 0 10px;
        border-bottom: solid #F1F1F3 1px;
        @include font-size(16px);
        .svg-icon {
          font-size: 2em;
          height: 32px;
          margin: 13px 0;
        }
        span {
          display: inline-block;
          text-align: left;
          width: 130px;
          margin: 0 10px;
        }
        label {
          cursor: pointer;
        }
      }
    }
    div.list-holder.scrollable-table table.listtable {
      th.type, td.type {
        @for $i from 1 through length($colors) {
          &:nth-child(#{length($colors)}n+#{$i + 1}):not(:nth-child(2)) {
            @include table-col-bg(0, nth($colors, $i));
          }
        }
      }
      th.time + th, td.time + td {
        @include table-col-bg(15px);
      }
    }
  }
</style>



// WEBPACK FOOTER //
// src/apps/feelgrid/modules/pureData/SensorView.vue